import React, { useRef, useEffect, useMemo, useState } from "react";
import {
    fetchDashboardOverview,
    fetchDashboardRecent,
    fetchDashboardTopTags,
    fetchDashboardAnalytics,
    fetchDashboardSystem,
    type DashboardSystemResponse,
} from "../../../api/dashboard";
import "../../../assets/styles/dashboard.scss";

type TagItem = { id: number; name: string; count: number };

export default function Dashboard() {
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const didLoad = useRef(false);
    const [showBoot, setShowBoot] = useState(() => !sessionStorage.getItem("bootShown"));

    const [ov, setOv] = useState<any>(null);
    const [recent, setRecent] = useState<any>(null);
    const [tags, setTags] = useState<{ items: TagItem[] } | null>(null);
    const [analytics, setAnalytics] = useState<{ items: Array<{ label: string; value: number | string }> } | null>(null);
    const [system, setSystem] = useState<DashboardSystemResponse | null>(null);
    const [systemRefreshing, setSystemRefreshing] = useState(false);

    const today = useMemo(() => new Date().toLocaleDateString(), []);

    async function load() {
        setLoading(true);
        setErr(null);
        try {
            const [o, r, t, a, s] = await Promise.all([
                fetchDashboardOverview(),
                fetchDashboardRecent(),
                fetchDashboardTopTags(),
                fetchDashboardAnalytics(),
                fetchDashboardSystem(),
            ]);
            setOv(o);
            setRecent(r);
            setTags(t);
            setAnalytics(a);
            setSystem(s);
        } catch (e: any) {
            console.error(e);
            setErr(e?.message || "Błąd ładowania dashboardu");
        } finally {
            setLoading(false);
        }
    }

    async function refreshSystem() {
        setSystemRefreshing(true);
        try {
            const s = await fetchDashboardSystem();
            setSystem(s);
        } catch (e) {
            console.error(e);
            // opcjonalnie: osobny komunikat o błędzie
            // setErr("Nie udało się odświeżyć statusu systemu");
        } finally {
            setSystemRefreshing(false);
        }
    }
    
    useEffect(() => {
        if (showBoot) {
            sessionStorage.setItem("bootShown", "1");
            const t = setTimeout(() => setShowBoot(false), 420);
            return () => clearTimeout(t);
        }
    }, [showBoot]);

    useEffect(() => {
        if (didLoad.current) return;
        didLoad.current = true;
        load();
    }, []);

    const timeline = useMemo(() => {
        if (!recent) return [];
        type Item = { ts: number; kind: 'content' | 'user' | 'error'; title: string };
        const items: Item[] = [];

        (recent.recent_contents || []).forEach((c: any) => {
            items.push({
                ts: new Date(c.created_at).getTime(),
                kind: 'content',
                title: `Dodano treść „${c.title}”`,
            });
        });
        (recent.recent_users || []).forEach((u: any) => {
            items.push({
                ts: new Date(u.created_at).getTime(),
                kind: 'user',
                title: `Nowy użytkownik ${u.email}`,
            });
        });
        (recent.recent_errors || []).forEach((e: any) => {
            items.push({
                ts: new Date(e.reported_at).getTime(),
                kind: 'error',
                title: `Zgłoszono błąd #${String(e.id).slice(0, 8)} (${e.type})`,
            });
        });

        return items.sort((a, b) => b.ts - a.ts).slice(0, 10);
    }, [recent]);

    function fmtUptime(seconds: number) {
        const d = Math.floor(seconds / 86400);
        const h = Math.floor((seconds % 86400) / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return d ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;
    }

    return (
        <div className="dash-root">
            {showBoot && <div className="boot-screen" aria-hidden="true" />}
            <header className="dash-header">
                <div className="title">
                    <h1>Dashboard</h1>
                    <p className="subline">Przegląd systemu CMS – aktualny stan na dzień {today}.</p>
                </div>

                <div className="toolbar">
                    <a className="btn primary" href="/admin/contents">
                        <span className="ico">{plusIcon()}</span>
                        <span className="label">Dodaj treść</span>
                    </a>
                    <button className="btn ghost" onClick={() => exportIconAction()}>
                        <span className="ico">{downloadIcon()}</span>
                        <span className="label">Eksport</span>
                    </button>
                </div>
            </header>

            {/* KPI */}
            <section className="kpi-grid">
                <KPI title="Użytkownicy" value={ov?.users_total} meta={ov ? `+${ov.users_new_last7} w 7 dni` : ''} icon={usersIcon()} />
                <KPI title="Premium aktywni" value={ov?.premium_active} meta={ov ? `${ov.premium_share}% użytkowników` : ''} icon={shieldIcon()} />
                <KPI title="Treści (widoczne)" value={ov?.contents_visible} meta={ov ? `${Math.round(((ov.contents_visible || 0) / Math.max(ov.contents_total || 1, 1)) * 100)}% widocznych` : ''} icon={eyeIcon()} />
                <KPI title="Treści premium" value={ov?.contents_premium} meta={ov ? `${Math.round(((ov.contents_premium || 0) / Math.max(ov.contents_total || 1, 1)) * 100)}% treści` : ''} icon={sparklesIcon()} />
                <KPI title="Błędy do akcji" value={ov?.errors_pending} meta={ov ? `+${ov.errors_new_last7} w 7 dni` : ''} icon={alertIcon()} danger />
                <KPI title="Śr. ocena" value={ov ? ov.ratings_avg.toFixed(2) : '—'} meta={ov ? `${ov.ratings_count} ocen` : ''} icon={starIcon()} />
                <KPI title="Odczyty 7d" value={ov?.reads_7d ?? '—'} meta={ov && ov.reads_7d ? '' : '—'} icon={bookIcon()} />
                <KPI title="Odsłuchy 7d" value={ov?.listens_7d ?? '—'} meta={ov && ov.listens_7d ? '' : '—'} icon={audioIcon()} />
            </section>

            {/* 2 kolumny: treści + błędy */}
            <section className="grid-2">
                <div className="card">
                    <div className="card-head">
                        <h2>Ostatnie treści</h2>
                        <a className="link" href="/admin/contents">Zobacz wszystkie {chevIcon()}</a>
                    </div>
                    <ul className="list">
                        {(recent?.recent_contents || []).map((c: any) => (
                            <li key={c.id} className="list-row">
                                <div className="list-main">
                                    <div className="strong">{c.title}</div>
                                    <div className="muted small">{new Date(c.created_at).toLocaleString()}</div>
                                </div>
                                <div className="list-tags">
                                    {c.is_visible ? <span className="pill ok">Public</span> : <span className="pill">Hidden</span>}
                                    {c.is_premium && <span className="pill warn">Premium</span>}
                                </div>
                            </li>
                        ))}
                        {recent && (recent.recent_contents || []).length === 0 && (
                            <li className="list-row muted">Brak danych</li>
                        )}
                    </ul>
                </div>

                <div className="card">
                    <div className="card-head">
                        <h2>Ostatnie zgłoszenia błędów</h2>
                        <a className="link" href="/admin/errors">Zarządzaj {chevIcon()}</a>
                    </div>
                    <ul className="list">
                        {(recent?.recent_errors || []).map((e: any) => (
                            <li key={e.id} className={`list-row ${e.is_resolved ? '' : 'row-highlight'}`}>
                                <div className="list-main">
                                    <div className="strong">#{String(e.id).slice(0, 8)} • {e.type}</div>
                                    <div className="muted small">{e.user_email || '—'} • {new Date(e.reported_at).toLocaleString()}</div>
                                </div>
                                <div className="list-tags">
                                    {e.is_resolved ? <span className="pill ok">OK</span> : <span className="pill danger">Akcja</span>}
                                </div>
                            </li>
                        ))}
                        {recent && (recent.recent_errors || []).length === 0 && (
                            <li className="list-row muted">Brak danych</li>
                        )}
                    </ul>
                </div>
            </section>

            {/* Nowi użytkownicy */}
            <section className="single-col">
                <div className="card">
                    <div className="card-head">
                        <h2>Nowi użytkownicy</h2>
                        <a className="link" href="/admin/users">Zarządzaj {chevIcon()}</a>
                    </div>
                    <ul className="list">
                        {(recent?.recent_users || []).map((u: any) => (
                            <li key={u.id} className="list-row">
                                <div className="list-main">
                                    <div className="strong">{u.email}</div>
                                    <div className="muted small">{new Date(u.created_at).toLocaleString()}</div>
                                </div>
                                <div className="list-tags">{u.is_premium && <span className="pill warn">Premium</span>}</div>
                            </li>
                        ))}
                        {recent && (recent.recent_users || []).length === 0 && (
                            <li className="list-row muted">Brak danych</li>
                        )}
                    </ul>
                </div>
            </section>

            {/* Top tagi + Analityka + Szybkie akcje */}
            <section className="grid-3-alt">
                <div className="card">
                    <div className="card-head"><h2>Top tagi</h2></div>
                    <div className="tags-inline">
                        {(tags?.items || []).length > 0 ? (
                            tags!.items.map(t => (
                                <span key={t.id} className="tag-chip">
                                    {t.name}<span className="chip-count">{t.count}</span>
                                </span>
                            ))
                        ) : (
                            <span className="muted">Brak tagów</span>
                        )}
                    </div>
                </div>

                <div className="card">
                    <div className="card-head"><h2>Analityka treści</h2></div>
                    <div className="metrics-table">
                        {(analytics?.items || []).map((m, i) => (
                            <div className="metric-row" key={i}>
                                <span className="metric-label">{m.label}</span>
                                <span className="metric-value">{m.value}</span>
                            </div>
                        ))}
                        {analytics && (analytics.items || []).length === 0 && (
                            <div className="metric-row muted">Brak danych</div>
                        )}
                    </div>
                </div>

                <div className="card">
                    <div className="card-head"><h2>Szybkie akcje</h2></div>
                    <div className="qa-vertical">
                        <a className="qa-button primary" href="/admin/contents">{plusIcon()}<span>Dodaj treść</span></a>
                        <a className="qa-button" href="/admin/users">{usersIcon()}<span>Użytkownicy</span></a>
                        <a className="qa-button" href="/admin/errors">{wrenchIcon()}<span>Błędy</span></a>
                        <a className="qa-button" href="/admin/tags">{tagIcon()}<span>Tagi</span></a>
                    </div>
                </div>
            </section>

            {/* Status systemu */}
            <section className="card status-card">
                <div className="card-head">
                    <h2>Status systemu</h2>
                    <button
                        type="button"
                        className="link"
                        onClick={refreshSystem}
                        disabled={systemRefreshing}
                    >
                        {systemRefreshing ? "Odświeżanie…" : <>Odśwież {refreshIcon()}</>}
                    </button>
                </div>
                <div className="status-grid">
                    <div className="status-item">
                        <div className="status-ico">{checkIcon()}</div>
                        <div className="status-title">Serwer aplikacji</div>
                        <div className="status-sub">{system?.app_status || '—'}</div>
                        <div className="status-meta">Uptime: {system ? fmtUptime(system.app_uptime_seconds) : '—'}</div>
                    </div>
                    <div className="status-item">
                        <div className="status-ico">{dbIcon()}</div>
                        <div className="status-title">Baza danych</div>
                        <div className="status-sub">{system?.db_status || '—'}</div>
                        <div className="status-meta">Odpowiedź: {system ? `${system.db_latency_ms}ms` : '—'}</div>
                    </div>
                    <div className="status-item">
                        <div className="status-ico">{cloudIcon()}</div>
                        <div className="status-title">Usługi CDN</div>
                        <div className="status-sub">{system?.cdn_status || 'UNKNOWN'}</div>
                        <div className="status-meta">{system?.cdn_status ? 'Monitorowane' : '—'}</div>
                    </div>

                    {/* CPU – proces */}
                    <div className="status-item">
                        <div className="status-ico">{cpuIcon()}</div>
                        <div className="status-title">CPU – proces</div>
                        <div className="status-sub">
                            {system ? `${system.cpu.process_percent.toFixed(1)}%` : '—'}
                        </div>
                        <div className="status-meta">
                            Rdzeni: {system ? system.cpu.cores : '—'}
                        </div>
                    </div>

                    {/* CPU – serwer */}
                    <div className="status-item">
                        <div className="status-ico">{cpuIcon()}</div>
                        <div className="status-title">CPU – serwer</div>
                        <div className="status-sub">
                            {system ? `${system.cpu.system_percent_approx}%` : '—'}
                        </div>
                        <div className="status-meta">
                            Load 1m: {system ? system.cpu.load_1m.toFixed(2) : '—'}
                        </div>
                    </div>

                    {/* Pamięć procesu */}
                    <div className="status-item">
                        <div className="status-ico">{ramIcon()}</div>
                        <div className="status-title">Pamięć</div>
                        <div className="status-sub">
                            {system ? `${system.memory.process_mb} MB` : '—'}
                        </div>
                        <div className="status-meta">Zużycie RAM przez API</div>
                    </div>
                </div>
            </section>

            {/* Ostatnia aktywność */}
            <section className="card">
                <div className="card-head"><h2>Ostatnia aktywność</h2></div>
                <ul className="timeline">
                    {timeline.map((t, idx) => (
                        <li key={idx} className="timeline-row">
                            <span className="dot">+</span>
                            <div className="t-main">
                                <div className="t-strong">{t.title}</div>
                                <div className="t-muted">{new Date(t.ts).toLocaleString()}</div>
                            </div>
                        </li>
                    ))}
                    {recent && timeline.length === 0 && (
                        <li className="timeline-row muted">Brak danych</li>
                    )}
                </ul>
            </section>

            {loading && <div className="alert">Ładowanie…</div>}
            {err && <div className="alert error">{err}</div>}
        </div>
    );
}

function KPI({ title, value, meta, danger, icon }: { title: string; value: any; meta?: string; danger?: boolean; icon?: React.ReactNode }) {
    return (
        <div className={`kpi ${danger ? 'kpi-danger' : ''}`}>
            <div className="kpi-ico">{icon}</div>
            <div className="kpi-title">{title}</div>
            <div className="kpi-value">{value ?? '—'}</div>
            {meta && <div className="kpi-meta">{meta}</div>}
        </div>
    );
}

/* ===== SVG ===== */
const svg = (p: string) => (
    <svg
        viewBox="0 0 24 24"
        width={16}
        height={16}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        dangerouslySetInnerHTML={{ __html: p }}
    />
);

const chevIcon = () => svg('<polyline points="9 18 15 12 9 6"/>');
const plusIcon = () => svg('<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>');
const downloadIcon = () => svg('<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>');
const tagIcon = () => svg('<path d="M20.59 13.41L11 3H4v7l9.59 9.59a2 2 0 0 0 2.82 0l4.18-4.18a2 2 0 0 0 0-2.82z"/><circle cx="7.5" cy="7.5" r="1.5"/>');
const wrenchIcon = () => svg('<path d="M14.7 6.3a4 4 0 1 0 3 3L22 3l-3-3-4.3 6.3z"/><path d="M2 22l7-7"/>');
const checkIcon = () => svg('<path d="M20 6L9 17l-5-5"/>');
const dbIcon = () => svg('<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>');
const cloudIcon = () => svg('<path d="M17.5 19a4.5 4.5 0 0 0 0-9 5.5 5.5 0 0 0-10.5 2A4 4 0 1 0 7 19z"/>');

const svgSolid = (p: string) => (
    <svg
        viewBox="0 0 24 24"
        width={20}
        height={20}
        fill="currentColor"
        stroke="none"
        dangerouslySetInnerHTML={{ __html: p }}
    />
);

const usersIcon = () => svgSolid('<circle cx="12" cy="9" r="3"/><path d="M6 20a6 6 0 0 1 12 0H6z"/><circle cx="7.5" cy="11" r="2"/><circle cx="16.5" cy="11" r="2"/><path d="M2 20a4.5 4.5 0 0 1 6.5-3.9V20H2z"/><path d="M15.5 16.1A4.5 4.5 0 0 1 22 20h-7v-3.9z"/>');
const shieldIcon = () => svgSolid('<path d="M5 10l3 3 4-6 4 6 3-3 3 2-2 8H4L2 12z"/><rect x="6" y="18" width="12" height="2" rx="1"/>');
const eyeIcon = () => svgSolid('<path d="M12 5c7 0 11 7 11 7s-4 7-11 7S1 12 1 12s4-7 11-7z"/><circle cx="12" cy="12" r="3" fill="#ffffff"/><circle cx="12" cy="12" r="1.6"/>');
const sparklesIcon = () => svgSolid('<path d="M7 4h10l4 5-9 11L3 9l4-5z"/><path d="M9.5 6l-2 3h9l-2-3h-5z" fill="#ffffff" opacity=".25"/>');
const alertIcon = () => svgSolid('<path d="M12 3a2 2 0 0 1 1.73 1L22 19a2 2 0 0 1-1.73 3H3.73A2 2 0 0 1 2 19l8.27-15A2 2 0 0 1 12 3z"/><rect x="11" y="9" width="2" height="6" rx="1" fill="#ffffff"/><circle cx="12" cy="17.5" r="1.4" fill="#ffffff"/>');
const starIcon = () => svgSolid('<path d="M12 3l2.9 5.9L21 10l-4.6 4.3.6 6.2L12 17.7 7 20.5l.6-6.2L3 10l6.1-1.1L12 3z"/>');
const bookIcon = () => svgSolid('<path d="M3 6a2 2 0 0 1 2-2h7v16H5a2 2 0 0 1-2-2V6z"/><path d="M12 4h7a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-7V4z"/>');
const audioIcon = () => svgSolid('<path d="M12 4a8 8 0 0 0-8 8v3a2 2 0 0 0 2 2h2v-6H6a6 6 0 0 1 12 0h-2v6h2a2 2 0 0 0 2-2v-3a8 8 0 0 0-8-8z"/>');

/* nowe ikonki dla CPU / RAM */
const cpuIcon = () =>
    svgSolid(
        '<rect x="4" y="4" width="16" height="16" rx="2"/>' +
        '<rect x="9" y="9" width="6" height="6" fill="#000000" opacity=".2"/>' +
        '<path d="M12 2v2M12 20v2M4 12H2M22 12h-2M7 2v2M17 2v2M7 20v2M17 20v2" />'
    );

const ramIcon = () =>
    svgSolid(
        '<rect x="3" y="7" width="18" height="10" rx="2"/>' +
        '<rect x="6" y="10" width="3" height="4" fill="#000000" opacity=".2"/>' +
        '<rect x="10.5" y="10" width="3" height="4" fill="#000000" opacity=".2"/>' +
        '<rect x="15" y="10" width="3" height="4" fill="#000000" opacity=".2"/>'
    );

const refreshIcon = () =>
    svg(
        '<polyline points="4 4 4 10 10 10"/>' +
        '<path d="M4 4a9 9 0 0 1 15.56 3"/>' +
        '<polyline points="20 20 20 14 14 14"/>' +
        '<path d="M20 20a9 9 0 0 1-15.56-3"/>'
    );

function exportIconAction() { /* hook na przyszłość */ }